import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Wallet, CreditCard, Banknote, Loader2, ArrowRight, CheckCircle2, Download, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

/**
 * Wallet card — shows current balance, lets buyer pick a top-up package + pay via card/bank.
 * Polls Stripe session on return from checkout to credit balance idempotently.
 */
export default function WalletCard() {
  const [wallet, setWallet] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pkg, setPkg] = useState("topup_5000");
  const [pm, setPm] = useState("card");
  const [customAmount, setCustomAmount] = useState("5000");
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try { const { data } = await api.get("/billing/wallet"); setWallet(data); } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  // If we returned from Stripe top-up checkout, poll status to credit balance
  useEffect(() => {
    const sid = params.get("topup_session");
    if (!sid) return;
    let attempt = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/topup/status/${sid}`);
        if (data?.payment_status === "paid") {
          toast.success(`Wallet topped up · balance £${data.balance.toLocaleString()}`);
          await load();
          const np = new URLSearchParams(params); np.delete("topup_session"); setParams(np, { replace: true });
          return;
        }
      } catch (_) {}
      attempt += 1;
      if (attempt < 20) setTimeout(poll, 2000);
    };
    poll();
  }, [params, setParams]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { package_id: pkg, payment_method: pm, origin_url: window.location.origin };
      if (pkg === "custom") {
        const amt = Number(customAmount);
        const min = wallet?.custom_topup?.min_gbp ?? 5000;
        const max = wallet?.custom_topup?.max_gbp ?? 50000;
        if (!amt || isNaN(amt) || amt < min || amt > max) {
          toast.error(`Enter an amount between £${min.toLocaleString()} and £${max.toLocaleString()}`);
          setBusy(false);
          return;
        }
        payload.custom_amount_gbp = amt;
      }
      const { data } = await api.post("/billing/topup", payload);
      if (data.payment_method === "bank_transfer") {
        toast.success(`Invoice ${data.invoice_number} emailed — pay within 7 days to credit wallet.`);
        setModalOpen(false);
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Top-up failed");
    } finally {
      setBusy(false);
    }
  };

  if (!wallet) return null;

  const balance = Number(wallet.balance || 0);
  const awaitingOrders = wallet.awaiting_orders || [];
  const awaitingTotal = Number(wallet.awaiting_total_gbp || 0);

  const openInvoicePdf = async (invoiceId) => {
    if (!invoiceId) return;
    try {
      const { data } = await api.get(`/invoices/${invoiceId}/share-link`);
      if (data?.url) window.open(data.url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open PDF");
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-soft p-6 min-w-0 overflow-hidden" data-testid="wallet-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><Wallet className="size-3.5" /> Buyer wallet</div>
          <div className="mt-2 text-3xl font-semibold tracking-tight tabular-nums text-slate-900 truncate" data-testid="wallet-balance">£{balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
          <div className="mt-1 text-[12px] text-slate-500">Prepaid lead spend · debits per accepted lead</div>
        </div>
        {wallet.low_balance && (
          <span className="shrink-0 rounded-full border border-amber-200 bg-amber-50 text-amber-700 text-[10px] font-medium px-2 py-0.5 uppercase tracking-widest" data-testid="wallet-low">Low</span>
        )}
        {awaitingTotal > 0 && (
          <span className="shrink-0 rounded-full border border-blue-200 bg-blue-50 text-blue-700 text-[10px] font-medium px-2 py-0.5 uppercase tracking-widest" data-testid="wallet-pending-badge">Pending</span>
        )}
      </div>

      {/* iter86: pending bank-transfer callout — explains WHY the wallet is £0 */}
      {awaitingTotal > 0 && (
        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50/60 p-3.5" data-testid="wallet-pending-callout">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-blue-700 font-semibold inline-flex items-center gap-1.5">
                <Banknote className="size-3" /> Awaiting bank transfer
              </div>
              <div className="mt-1 text-[13px] text-blue-900 font-medium tabular-nums">
                £{awaitingTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })} will land here
              </div>
              <div className="mt-0.5 text-[11.5px] text-blue-700/80 leading-snug">
                We'll activate your campaign and credit your wallet as soon as funds clear.
              </div>
            </div>
          </div>
          <ul className="mt-3 space-y-1.5">
            {awaitingOrders.slice(0, 3).map((o) => (
              <li
                key={o.id}
                className="flex items-center justify-between gap-2 rounded-md bg-white/60 border border-blue-100 px-2.5 py-1.5"
                data-testid={`wallet-pending-${(o.invoice_number || "").slice(-6)}`}
              >
                <div className="min-w-0">
                  <div className="text-[11.5px] font-mono text-slate-900 truncate">{o.invoice_number || o.id?.slice(0, 8)}</div>
                  <div className="text-[11px] text-slate-500 truncate">{o.quantity_ordered} × {o.sector_name}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11.5px] tabular-nums text-slate-700">£{Number(o.total_gbp).toLocaleString()}</span>
                  {o.transfer_sent_at ? (
                    <span
                      className="inline-flex items-center gap-0.5 text-emerald-700 text-[11px] font-medium"
                      data-testid={`wallet-pending-reported-${(o.invoice_number || "").slice(-6)}`}
                      title="You've reported the transfer — our team has been notified"
                    >
                      <CheckCircle2 className="size-3" /> Reported
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openInvoicePdf(o.invoice_id)}
                      className="text-blue-700 hover:text-blue-900 inline-flex items-center gap-0.5 text-[11px] font-medium"
                      data-testid={`wallet-pending-invoice-${(o.invoice_number || "").slice(-6)}`}
                    >
                      <Download className="size-3" /> Invoice
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {wallet.low_balance && (
        <p className="mt-3 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Top up to keep leads flowing. Delivery pauses if your balance hits zero.
        </p>
      )}

      <Button onClick={() => setModalOpen(true)} className="mt-5 w-full rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="wallet-topup-btn">
        Top up wallet <ArrowRight className="size-4" />
      </Button>

      <div className="mt-5 pt-5 border-t border-slate-200">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Recent activity</div>
        {(wallet.ledger || []).slice(0, 6).length === 0 ? (
          <div className="text-[12px] text-slate-400 italic">No activity yet</div>
        ) : (
          <ul className="space-y-1.5 text-[12.5px]">
            {wallet.ledger.slice(0, 6).map((l) => (
              <li key={l.id} className="flex items-center justify-between gap-2 min-w-0">
                <span className="text-slate-600 truncate min-w-0 flex-1"><span className={l.type === "credit" ? "text-emerald-600" : "text-rose-600"}>{l.type === "credit" ? "+" : "−"}</span> £{Number(l.amount).toLocaleString()} · {l.source?.replace(/_/g, " ")}</span>
                <span className="text-slate-400 text-[11px] font-mono shrink-0">{new Date(l.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md" data-testid="topup-modal">
          <DialogHeader>
            <DialogTitle>Top up wallet</DialogTitle>
            <DialogDescription>Prepaid · debits on each accepted lead · 20% VAT applied on bank transfer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {wallet.launch_partner?.qualified && (
              <div className="rounded-md border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4" data-testid="launch-partner-badge">
                <div className="flex items-start gap-3">
                  <span className="inline-flex size-9 items-center justify-center rounded-sm bg-emerald-600 text-white shrink-0">
                    <Sparkles className="size-4" strokeWidth={1.6} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-emerald-900 leading-tight">
                      Launch Partner +{Math.round((wallet.launch_partner.bonus_pct||0.20)*100)}% bonus active
                    </div>
                    <div className="text-[12px] text-emerald-800/85 mt-1 leading-snug">
                      Your first top-up of <strong>£{Math.round(wallet.launch_partner.min_topup||5000).toLocaleString()}</strong> or more credits an extra <strong>{Math.round((wallet.launch_partner.bonus_pct||0.20)*100)}%</strong> wallet bonus on top, automatically.
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Amount</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {(wallet.packages || []).map((p) => (
                  <button key={p.id} onClick={() => setPkg(p.id)} className={`rounded-xl border px-3 py-3 text-center transition-all ${pkg === p.id ? "border-[#0055FF] bg-[#0055FF]/[0.04] ring-1 ring-[#0055FF]/20" : "border-slate-200 hover:border-slate-300"}`} data-testid={`topup-${p.id}`}>
                    <div className="text-base font-semibold text-slate-900 tabular-nums">£{p.amount.toLocaleString()}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">top-up</div>
                  </button>
                ))}
                <button onClick={() => setPkg("custom")} className={`rounded-xl border px-3 py-3 text-center transition-all ${pkg === "custom" ? "border-[#0055FF] bg-[#0055FF]/[0.04] ring-1 ring-[#0055FF]/20" : "border-slate-200 hover:border-slate-300"}`} data-testid="topup-custom">
                  <div className="text-base font-semibold text-slate-900 tabular-nums">Custom</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">£{Math.round(wallet.custom_topup?.min_gbp || 5000).toLocaleString()}–£{Math.round(wallet.custom_topup?.max_gbp || 50000).toLocaleString()}</div>
                </button>
              </div>
              {pkg === "custom" && (
                <div className="mt-3">
                  <label className="text-[11px] uppercase tracking-widest text-slate-500 block mb-1.5">Custom amount (£)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                    <input
                      type="number"
                      min={wallet.custom_topup?.min_gbp || 5000}
                      max={wallet.custom_topup?.max_gbp || 50000}
                      step={100}
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 pl-7 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF]/20 focus:border-[#0055FF] tabular-nums"
                      placeholder="5000"
                      data-testid="topup-custom-input"
                    />
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1.5">
                    Min £{Math.round(wallet.custom_topup?.min_gbp || 5000).toLocaleString()} · Max £{Math.round(wallet.custom_topup?.max_gbp || 50000).toLocaleString()} · Larger amounts? <a href="mailto:billing@connex.is" className="text-[#0055FF] hover:underline">Email billing</a>
                  </p>
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Payment method</div>
              <div className="grid grid-cols-2 gap-2">
                <PMButton active={pm === "card"} onClick={() => setPm("card")} Icon={CreditCard} title="Card" subtitle="Stripe · instant" testid="topup-pm-card" />
                <PMButton active={pm === "bank_transfer"} onClick={() => setPm("bank_transfer")} Icon={Banknote} title="Bank transfer" subtitle="PDF invoice · 7-day" testid="topup-pm-bank" />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="outline" onClick={() => setModalOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={submit} disabled={busy} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="topup-submit">
              {busy ? <><Loader2 className="size-4 animate-spin" /> Processing…</> : <>Continue <ArrowRight className="size-4" /></>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PMButton({ active, onClick, Icon, title, subtitle, testid }) {
  return (
    <button type="button" onClick={onClick} className={`text-left rounded-xl border px-3.5 py-3 transition-all ${active ? "border-[#0055FF] bg-[#0055FF]/[0.04] ring-1 ring-[#0055FF]/20" : "border-slate-200 hover:border-slate-300"}`} data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className={`inline-grid place-items-center size-7 rounded-md ${active ? "bg-[#0055FF] text-white" : "bg-slate-100 text-slate-700"}`}><Icon className="size-3.5" strokeWidth={1.7} /></span>
        <span className="font-medium text-slate-900 text-[13px]">{title}</span>
      </div>
      <p className="text-[11px] text-slate-500 mt-1.5 pl-9">{subtitle}</p>
    </button>
  );
}
